import { BaseApiService } from './BaseApiService';

export interface Permission {
  id: string;
  name: string;
  category: 'admin' | 'reports' | 'page_access';
  description: string;
}

export interface Role {
  id: number;
  name: string;
  description: string;
  permissions: string[];
  created_at: string;
  updated_at: string;
}

export interface CreateRoleData {
  name: string;
  description: string;
  permissions: string[];
}

export interface UpdateRoleData {
  name?: string;
  description?: string;
  permissions?: string[];
}

export interface UserWithRole {
  id: number;
  username: string;
  email: string;
  role: string;
  role_id?: number;
  role_name?: string;
  permissions?: string[];
  created_at: string;
  updated_at: string;
}

export class RoleService extends BaseApiService {
  // Get all roles
  async getRoles(): Promise<Role[]> {
    const response = await this.get<{ success: boolean; data: Role[] }>('/admin/roles');
    return response.data;
  }

  // Get all permissions
  async getPermissions(): Promise<Permission[]> {
    const response = await this.get<{ success: boolean; data: Permission[] }>('/admin/permissions');
    return response.data;
  }

  // Get permissions by category
  async getPermissionsByCategory(
    category: 'admin' | 'reports' | 'page_access'
  ): Promise<Permission[]> {
    const permissions = await this.getPermissions();
    return permissions.filter((p) => p.category === category);
  }

  // Create a new role
  async createRole(roleData: CreateRoleData): Promise<Role> {
    const response = await this.post<{ success: boolean; data: Role }>('/admin/roles', roleData);
    return response.data;
  }

  // Update a role
  async updateRole(roleId: number, updates: UpdateRoleData): Promise<Role> {
    const response = await this.put<{ success: boolean; data: Role }>(
      `/admin/roles/${roleId}`,
      updates
    );
    return response.data;
  }

  // Delete a role
  async deleteRole(roleId: number): Promise<void> {
    await this.delete(`/admin/roles/${roleId}`);
  }

  // Get users with their roles
  async getUsersWithRoles(): Promise<UserWithRole[]> {
    const response = await this.get<{ success: boolean; data: UserWithRole[] }>('/admin/users');
    return response.data;
  }

  // Update user role
  async updateUserRole(userId: number, roleId: number | null): Promise<UserWithRole> {
    const response = await this.put<{ success: boolean; data: UserWithRole }>(
      `/admin/users/${userId}/role`,
      { role_id: roleId }
    );
    return response.data;
  }

  // Check if user has permission
  static hasPermission(userPermissions: string[], permission: string): boolean {
    return userPermissions.includes(permission);
  }

  // Check if user has any of the specified permissions
  static hasAnyPermission(userPermissions: string[], permissions: string[]): boolean {
    return permissions.some((permission) => userPermissions.includes(permission));
  }

  // Check if user has all of the specified permissions
  static hasAllPermissions(userPermissions: string[], permissions: string[]): boolean {
    return permissions.every((permission) => userPermissions.includes(permission));
  }

  // Get permission categories for UI grouping
  static getPermissionCategories(): { [key: string]: { name: string; description: string } } {
    return {
      admin: {
        name: 'Administration',
        description: 'System administration and management functions',
      },
      reports: {
        name: 'Reports',
        description: 'Access to various reports and analytics',
      },
      page_access: {
        name: 'Page Access',
        description: 'Access to specific pages and features',
      },
    };
  }
}

// Create a singleton instance for easy use
export const roleService = new RoleService();
